import { useState } from 'react';
import { Box, Grid, Popover, Typography, IconButton, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import GroupIcon from '@mui/icons-material/Group';
import ShieldIcon from '@mui/icons-material/Shield';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import DnsIcon from '@mui/icons-material/Dns';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CompressIcon from '@mui/icons-material/Compress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import ActionBox from './ActionBox';
import VMListModal from './VMListModal';

/**
 * Componente che renderizza il pannello di dettaglio espanso per un laboratorio.
 */
const LabDetailPanel = ({ lab, onAction, onClose, apiAuthGroups, onLabUpdate, onRefresh }) => {
    const [showVMModal, setShowVMModal] = useState(false);
    const [isTogglingPredefinito, setIsTogglingPredefinito] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isShrinking, setIsShrinking] = useState(false);
    const [showShrinkConfirm, setShowShrinkConfirm] = useState(false);

    const handleRefresh = async () => {
        if (isRefreshing || !onRefresh) return;
        setIsRefreshing(true);
        try {
            await onRefresh(lab.idLab);
        } catch (error) {
            console.error('Errore refresh lab:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const isAdmin = apiAuthGroups?.includes('ADMIN');
    const canTogglePredefinito = isAdmin && lab.labVersion === 2 && lab.usageData?.provisioningType === 'FLOATING' && lab.status === 'complete';

    const handleTogglePredefinito = async () => {
        if (isTogglingPredefinito) return;
        setIsTogglingPredefinito(true);
        try {
            const newValue = !lab.isPredefinito;
            const response = await fetch(process.env.REACT_APP_URL_SET_PREDEFINITO, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idLab: lab.idLab, isPredefinito: newValue }),
            });
            const data = await response.json();
            if (data.statusRequest === 'success' && onLabUpdate) {
                onLabUpdate({ ...lab, isPredefinito: newValue });
            }
        } catch (error) {
            console.error('Errore toggle predefinito:', error);
        } finally {
            setIsTogglingPredefinito(false);
        }
    };

    const canShrinkLab = isAdmin && lab.labVersion === 2 && lab.usageData?.provisioningType === 'FLOATING' && lab.status === 'complete';

    const handleShrinkLab = async () => {
        if (isShrinking) return;
        setIsShrinking(true);
        setShowShrinkConfirm(false);
        try {
            const response = await fetch(process.env.REACT_APP_URL_SHRINK_LAB, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idLab: lab.idLab }),
            });
            const data = await response.json();
            if (data.statusRequest !== 'success') {
                console.error('Errore shrink lab:', data.message);
            }
        } catch (error) {
            console.error('Errore shrink lab:', error);
        } finally {
            setIsShrinking(false);
        }
    };

    // Logica per disabilitare le azioni, isolata qui
    const isModificaDisabled = (lab.labVersion === 2 && ['error', 'dropped', 'deleted', 'creatingLab', 'refreshingLab', 'deletingLab', 'republishingLab'].includes(lab.status))
        || (lab.labVersion !== 2 && !['complete', 'error'].includes(lab.status));
    const isPianificazioneDisabled = ['dropped', 'deleted', 'error'].includes(lab.status);
    const isCancellaDisabled = !['complete'].includes(lab.status);
    const isCloneDisabled = !['complete', 'dropped', 'deleted', 'error'].includes(lab.status);
    const showRiesegui = lab.labVersion === 2 && lab.status === "error";
    const showGestisciUtenti = lab.labVersion === 2;
    const showGestisciProxy = lab.labVersion === 2 && lab.labType === "exam";
    const showGestisciAppVol = lab.labVersion === 2 && lab.baseImage?.hasAppVolAgent;

    const actions = [
        { id: 'modifica', icon: <SettingsIcon />, label: 'Modifica', onClick: () => onAction('modifica', lab), disabled: isModificaDisabled },
        { id: 'pianifica', icon: <CalendarMonthIcon />, label: 'Pianifica', onClick: () => onAction('pianifica', lab), disabled: isPianificazioneDisabled },
        { id: 'cancella', icon: <DeleteOutlineIcon />, label: 'Cancella', onClick: () => onAction('cancella', lab), disabled: isCancellaDisabled },
        { id: 'clona', icon: <ContentCopyIcon />, label: 'Clona', onClick: () => onAction('clona', lab), disabled: isCloneDisabled },
    ];

    if (showRiesegui) {
        actions.push({ id: 'riesegui', icon: <ReplayIcon />, label: 'Riesegui', onClick: () => onAction('riesegui', lab), disabled: false });
    }

    if (showGestisciUtenti) {
        actions.push({ id: 'gestisciUtenti', icon: <GroupIcon />, label: 'Utenti', onClick: () => onAction('gestisciUtenti', lab), disabled: false });
    }

    if (showGestisciProxy) {
        actions.push({ id: 'gestisciProxy', icon: <ShieldIcon />, label: 'Proxy', onClick: () => onAction('gestisciProxy', lab), disabled: false });
    }

    if (showGestisciAppVol) {
        actions.push({ id: 'gestisciAppVol', icon: <Inventory2Icon />, label: 'AppVol', onClick: () => onAction('gestisciAppVol', lab), disabled: false });
    }

    if (canShrinkLab) {
        actions.push({ id: 'shrinkLab', icon: <CompressIcon />, label: 'Shrink', onClick: () => setShowShrinkConfirm(true), disabled: isShrinking });
    }

    // Calcola il numero di docenti e studenti
    const numTeachers = Array.isArray(lab.teachers) ? lab.teachers.filter(t => t.registrationStatus === 'registered').length : 0;
    const numStudents = Array.isArray(lab.students) ? lab.students.filter(s => s.registrationStatus === 'registered').length : 0;

    // Data di scadenza del laboratorio: alla scadenza il laboratorio verra' eliminato
    const expirationDate = lab.expirationDate ? new Date(lab.expirationDate) : null;
    const formattedExpiration = expirationDate && !isNaN(expirationDate)
        ? expirationDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
        : null;

    return (
        <Box sx={{
            p: { xs: 1.5, sm: 2 },
            background: 'rgba(255,255,255,0.95)',
            width: '100%',
            boxSizing: 'border-box',
            position: 'relative',
        }}>
            {/* Pulsanti refresh e chiudi */}
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                {onRefresh && (
                    <IconButton
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        sx={{
                            color: '#666',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                        }}
                        size="small"
                    >
                        <RefreshIcon sx={{
                            animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                        }} />
                    </IconButton>
                )}
                {onClose && (
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#666',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                        }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            {/* Summary informativo del laboratorio */}
            <Box sx={{
                mb: 1.5,
                p: 1.5,
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
            }}>
                <Typography
                    sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        color: '#a71e3b',
                        fontSize: '0.95rem',
                        wordBreak: 'break-word',
                        pr: 3,
                    }}
                >
                    {lab.labName}
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">ID</Typography>
                        <Typography variant="body2" fontWeight="bold">{lab.idLab}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">Immagine</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{lab.baseImage?.name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">Size</Typography>
                        <Typography variant="body2" fontWeight="bold">{lab.baseImage?.size || '-'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">Provisioning</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {lab.usageData?.provisioningType === 'FLOATING' ? 'Floating' : 'Dedicated'}
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="textSecondary">Utenti</Typography>
                        <Typography variant="body2" fontWeight="bold">{numTeachers} doc, {numStudents} stud</Typography>
                    </Grid>
                    {lab.usageData?.observedUsers != null && (
                        <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <VisibilityIcon sx={{ fontSize: '0.85rem' }} /> Utenti osservati
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">{lab.usageData.observedUsers}</Typography>
                        </Grid>
                    )}
                    {lab.labDescription && (
                        <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary">Descrizione</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{lab.labDescription}</Typography>
                        </Grid>
                    )}
                    {canTogglePredefinito && (
                        <Grid item xs={4}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={lab.isPredefinito || false}
                                        onChange={handleTogglePredefinito}
                                        disabled={isTogglingPredefinito}
                                        size="small"
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#a71e3b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#a71e3b' } }}
                                    />
                                }
                                label={<Typography variant="body2" fontWeight="bold">Predefinito</Typography>}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Data di scadenza con warning discreto (o avviso di lab eliminato) */}
            {lab.status === 'deleted' ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1.5,
                    px: 1,
                    py: 0.5,
                    color: '#9a6700',
                }}>
                    <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        Il laboratorio &egrave; stato eliminato
                    </Typography>
                </Box>
            ) : formattedExpiration && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1.5,
                    px: 1,
                    py: 0.5,
                    color: '#9a6700',
                }}>
                    <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        Scadenza: <strong>{formattedExpiration}</strong> &mdash; il laboratorio verr&agrave; eliminato in questa data
                    </Typography>
                </Box>
            )}

            {/* Informazioni */}
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                Informazioni
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <PopupState variant="popover" popupId={`popover-accesso-${lab.idLab}`}>
                    {(popupState) => (
                        <>
                            <Box {...bindTrigger(popupState)}>
                                <ActionBox icon={<OpenInNewIcon />} label="Accesso" />
                            </Box>
                            <Popover
                                {...bindPopover(popupState)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                PaperProps={{ sx: { maxWidth: '400px', wordBreak: 'break-all' } }}
                            >
                                <Typography sx={{ p: 2, fontSize: '0.875rem' }}>
                                    Indirizzo: <strong>{lab.baseImage?.destination?.url}</strong>
                                    <a target="_blank" rel="noopener noreferrer" href={lab.baseImage?.destination?.url} style={{ marginLeft: '8px' }}><LinkIcon fontSize="small" /></a>
                                </Typography>
                            </Popover>
                        </>
                    )}
                </PopupState>
                <PopupState variant="popover" popupId={`popover-iscrizioni-${lab.idLab}`}>
                    {(popupState) => (
                        <>
                            <Box {...bindTrigger(popupState)}>
                                <ActionBox icon={<HowToRegIcon />} label="Iscrizioni" disabled={!lab.registerUrls} />
                            </Box>
                            <Popover
                                {...bindPopover(popupState)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                PaperProps={{ sx: { maxWidth: '500px', wordBreak: 'break-all' } }}
                            >
                                <Box sx={{ p: 2, fontSize: '0.875rem' }}>
                                    <strong>Link per l'iscrizione:</strong>
                                    <ul style={{ paddingLeft: '16px', margin: '8px 0' }}>
                                        {lab.registerUrls?.map((elem, index) => (
                                            <li key={index} style={{ marginBottom: '8px' }}>
                                                <strong>Docente:</strong> {elem.teacher.email}<br />
                                                <strong>URL:</strong> <span style={{ wordBreak: 'break-all' }}>{process.env.REACT_APP_URL_REGISTER_LAB_FRONTEND}/{elem.registerUrl}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Box>
                            </Popover>
                        </>
                    )}
                </PopupState>
                <Box onClick={() => setShowVMModal(true)} sx={{ cursor: 'pointer' }}>
                    <ActionBox icon={<DnsIcon />} label="VM" />
                </Box>
            </Box>

            {/* Strumenti */}
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                Strumenti
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {actions.map(action => (
                    <ActionBox key={action.id} {...action} />
                ))}
            </Box>

            {/* Modal per elenco VM */}
            <VMListModal
                open={showVMModal}
                onClose={() => setShowVMModal(false)}
                idLab={lab.idLab}
                labType={lab.labType}
            />

            {/* Dialog conferma Shrink Lab */}
            <Dialog open={showShrinkConfirm} onClose={() => setShowShrinkConfirm(false)}>
                <DialogTitle>Shrink Lab</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Ridurre il pool Horizon al minimo per il laboratorio <strong>{lab.labName}</strong>?
                        Il numero massimo di macchine verr&agrave; impostato al valore minimo possibile.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowShrinkConfirm(false)}>Annulla</Button>
                    <Button onClick={handleShrinkLab} color="error" variant="contained">Shrink</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LabDetailPanel;
